/**
 * Defensive predicate for the post-overlay mic-restart fallback (#825).
 *
 * Background — pre-#825 the server's `domino_quit` / `photo_closed`
 * / `video_stopped` handlers sent the overlay-specific
 * end message but did NOT send `{type: "listening"}` to bring the mic
 * back. The tablet was left with `audioServerDoneRef` false and the
 * mic soft-muted from Berta's in-overlay narration — the user spoke
 * to a dead WebSocket until they hung up.
 *
 * The server-side fix is `_after_overlay_close(websocket)` in
 * `app/api/voice.py`, called from every overlay-dismiss path. This
 * tablet-side fallback adds belt-and-braces coverage: if a future
 * fifth overlay forgets the server-side hook, the tablet still
 * recovers — TabletApp wires a `useEffect` to the overlay states
 * and schedules a 1 s timer per transition; when the timer fires it
 * calls this predicate to decide whether to invoke `startListening`.
 *
 * Pure function so it can be unit-tested without mounting all of
 * TabletApp.js. The TabletApp `useEffect` provides the
 * fresh-from-refs argument bag.
 *
 * @param {{
 *   dominoState: unknown,
 *   photoCarousel: unknown,
 *   videoOverlay: unknown,
 *   callState: string,
 *   status: string,
 *   micRecording: boolean,
 *   hasStartListening: boolean,
 * }} args  current overlay states + status + mic-recording flag.
 * @returns {boolean}  true when the fallback should call
 *   `startListening()`. False when the overlay is still open, when
 *   the call isn't connected, when a normal listening cycle is
 *   already underway, or when the mic is already recording.
 */
export function shouldFireOverlayCloseMicFallback({
  dominoState,
  photoCarousel,
  videoOverlay,
  callState,
  status,
  micRecording,
  hasStartListening,
}) {
  if (dominoState || photoCarousel || videoOverlay) {
    return false;
  }
  if (callState !== 'connected') return false;
  if (!hasStartListening) return false;
  if (micRecording) return false;
  if (
    status === 'transcribing' ||
    status === 'thinking' ||
    status === 'speaking'
  ) {
    return false;
  }
  return true;
}
